import React, { useState, useEffect } from "react";
import { logoutUser } from 'utils/authUtils';
import { useAuth } from 'contexts/AuthContext';
import {
  Box,
  Flex,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useColorModeValue,
  useDisclosure,
  IconButton,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Avatar,
  Circle,
  VStack,
  HStack,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Image
} from "@chakra-ui/react";
import { HamburgerIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
// import { SidebarContext } from "contexts/SidebarContext";
// import { useContext } from "react";
import PropTypes from "prop-types";
import { fetchProfileInfo, fetchUserProfilePicture } from 'utils/api';


// Main Sidebar Component
function Sidebar({ routes = [], children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(true); // Default to open for larger screens
  const [currentDate, setCurrentDate] = useState("");
  // const { isCollapsed, setIsCollapsed } = useContext(SidebarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const textColor = useColorModeValue("navy.700", "white");
  const [profile, setProfile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const empCode = localStorage.getItem("emp_code");

  // Sign Out Confirmation Modal State
  const {
    isOpen: isLogoutOpen,
    onOpen: onLogoutOpen,
    onClose: onLogoutClose
  } = useDisclosure();
  const cancelRef = React.useRef();



  let variantChange = "0.3s ease-in-out";

  let sidebarBg = useColorModeValue("white", "navy.800");
  useEffect(() => {
    const fetchProfileInfoDetails = async () => {
      try {
        if (empCode) {
          const data = await fetchProfileInfo(empCode);
          setProfile(data);
        }
      } catch (error) {
        console.error("Error fetching profile info:", error);
      }
    };

    fetchProfileInfoDetails();
  }, [empCode]);

  useEffect(() => {
    const loadProfilePicture = async () => {
      const imageUrl = await fetchUserProfilePicture(empCode);
      if (imageUrl) setProfilePhoto(imageUrl);
    };
    loadProfilePicture();
  }, [empCode]);
  // Function to format current date
  const formatCurrentDate = () => {
    const date = new Date();
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Set current date when component mounts
  useEffect(() => {
    setCurrentDate(formatCurrentDate());

    // Update date at midnight
    const updateDateAtMidnight = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const timeUntilMidnight = tomorrow - now;

      const timer = setTimeout(() => {
        setCurrentDate(formatCurrentDate());
        updateDateAtMidnight();
      }, timeUntilMidnight);

      return () => clearTimeout(timer);
    };

    updateDateAtMidnight();

    // Check screen size to set initial sidebar state
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    };

    // Set initial state
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle menu visibility
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle navigation and close menu on mobile
  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      setIsMenuOpen(false);
    }
  };

  // Handle sign out
  const handleSignOut = () => {

    logoutUser({ navigate, setAuth });

    // logoutUser({ navigate });
  };

  // Handle my profile navigation
  const handleMyProfile = () => {
    navigate('/admin/profile');
  };

  // Process routes to get a flat list of all navigable routes
  const getNavigableRoutes = (routesList) => {
    if (!routesList || !Array.isArray(routesList)) {
      return [];
    }

    let navigableRoutes = [];

    routesList.forEach(route => {
      if (!route) return;

      if (route.category && route.items) {
        navigableRoutes = [...navigableRoutes, ...getNavigableRoutes(route.items)];
      }
      else if (route.layout && route.path && route.name !== "Sign Out" && route.name !== "My Profile") {
        navigableRoutes.push({
          name: route.name,
          icon: route.icon,
          fullPath: `${route.layout}/${route.path}`
        });
      }
    });

    return navigableRoutes;
  };

  // Get flat list of navigable routes
  const navigableRoutes = getNavigableRoutes(routes);

  // Calculate sidebar width for consistent values
  const sidebarWidth = isMenuOpen ? "240px" : "60px";

  // Check if route is active
  const isRouteActive = (routePath) => {
    return location.pathname === routePath;
  };

  // Use the exact width values to avoid calculation issues
  const bgStyles = {
    background: '#F7FAFC', // Light background color for the light blue area
    minHeight: '100%'
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <Flex
        position="fixed"
        top="0"
        left="0"
        right="0"
        bg="white"
        borderBottom="1px solid #edf2f7"
        zIndex="1000"
        h="60px"
        alignItems="center"
        justifyContent="space-between"
        px="4"
      >
        {/* Left side - TIMESNAP heading */}
        <Flex alignItems="center">
          <Image src="/CI_Icon.ico" alt="TimeSnap Icon" boxSize="35px" mr="2" />
          <Text fontSize="2xl" fontWeight="bold" color="black.500">
            TimeSnap
          </Text>
        </Flex>

        {/* Right side - Session Date first, then Sign Out */}
        <Flex alignItems="center">
          {/* Session Date - Now first */}
          <Text mr="4" color="gray.600" fontWeight="bold">
            Session Date: {currentDate}
          </Text>

          {/* Logout Icon Button with Power Icon - Now second */}
          <Tooltip
            label="Sign Out"
            placement="bottom"
            hasArrow
            bg="#2D3748"
            color="white"
            borderRadius="md"
            fontSize="sm"
            fontWeight="medium"
            p="2"
          >
            <IconButton
              aria-label="Sign Out"
              icon={
                <Box as="span" display="flex" alignItems="center" justifyContent="center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                    <line x1="12" y1="2" x2="12" y2="12"></line>
                  </svg>
                </Box>
              }
              onClick={onLogoutOpen}
              variant="ghost"
              color="gray.600"
              _hover={{ color: "red.500", bg: "gray.100" }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* Main Layout Container */}
      <Flex position="relative" h="100vh">
        {/* Sidebar Fixed Container */}
        <Box
          bg={sidebarBg}
          transition={variantChange}
          w={sidebarWidth}
          h="full"
          position="fixed"
          top="60px"
          left="0"
          borderRight="1px solid #edf2f7"
          zIndex="900"
          overflowY="auto"
        >
          {/* Toggle Menu Button */}
          <Flex justifyContent={isMenuOpen ? "flex-end" : "center"} px="3" py="4" padding={"3px 8px 0px 6px"}>
            <IconButton
              aria-label="Toggle Menu"
              icon={<HamburgerIcon />}
              onClick={toggleMenu}
              transition={variantChange}
              size="sm"
              variant="outline"
            />
          </Flex>

          {/* Sidebar user profile - Only show when expanded */}
          {isMenuOpen && (
            <Box
              position="relative"
              cursor='pointer'
              p="5px">
              <Flex direction="column" alignItems="center" justifyContent="center">
                <Avatar size="xl" color="black" bg="green.100"
                  src={profilePhoto || undefined}
                  name={profile ? profile.user_name : "Loading..."} />
                <Text fontSize="lg" fontWeight="bold" color={textColor} mt={3} textAlign="center">
                  {profile ? profile.user_name : "Loading..."}
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  textAlign="center"
                  whiteSpace="normal"
                  wordBreak="break-all"
                  maxW={{ base: "200px", md: "250px", lg: "300px" }}
                >
                  {profile ? profile.email_Id : "Loading..."}
                </Text>

              </Flex>
            </Box>
          )}


          {/* Sidebar navigation content */}
          <Box className="sidebar-content" mt="2">
            {/* When Menu is open - show full content */}
            {isMenuOpen ? (
              <Box px="3">
                <VStack spacing={1} align="start" w="100%">
                  {navigableRoutes.map((route, index) => (
                    <Flex
                      key={index}
                      w="100%"
                      py="2"
                      px="3"
                      cursor="pointer"
                      borderRadius="md"
                      bg={isRouteActive(route.fullPath) ? "green.50" : "transparent"}
                      color={isRouteActive(route.fullPath) ? "black" : "inherit"}
                      _hover={{ bg: isRouteActive(route.fullPath) ? "green.100" : "green.100" }}
                      onClick={() => handleNavigation(route.fullPath)}
                      alignItems="center"
                    >
                      {route.icon}
                      <Text ml="3" fontSize="sm" color={isRouteActive(route.fullPath) ? "black" : "inherit"} fontWeight={isRouteActive(route.fullPath) ? "bold" : "normal"}>
                        {route.name}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            ) : (
              /* When Menu is collapsed - show only icons */
              <VStack spacing={6} align="center" pt={2}>
                {navigableRoutes.map((route, index) => (
                  <Tooltip
                    key={index}
                    label={route.name}
                    placement="right"
                    hasArrow
                    bg="#FFFFFFFF" // Light black background color
                    color="black" // White text color
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="medium"
                    p="2"
                  >
                    <IconButton
                      aria-label={route.name}
                      icon={route.icon}
                      onClick={() => handleNavigation(route.fullPath)}
                      variant="ghost"
                      color={isRouteActive(route.fullPath) ? "green.600" : "inherit"}
                      bg={isRouteActive(route.fullPath) ? "green.50" : "transparent"}
                      _hover={{ bg: isRouteActive(route.fullPath) ? "green.100" : "green.100" }}
                      fontSize="16px"
                      size="sm"
                    />
                  </Tooltip>
                ))}
              </VStack>
            )}
          </Box>
        </Box>

        {/* Content Area - With exact width calculation */}
        <Box
          ml={sidebarWidth}
          transition={variantChange}
          width={{ base: '100%', md: `calc(100% - ${sidebarWidth})` }}
          minH="calc(100vh - 60px)"   // ⬅️ IMPORTANT
          overflowY="auto"
          // pt="30px"
          bg='white'
        >

          {/* Content Wrapper with padding */}
          <Box p='0'>
            {/* <Box px="12px"> */}
            {children}
          </Box>
        </Box>
      </Flex>
      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={cancelRef}
        onClose={onLogoutClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Sign Out
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to sign out?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onLogoutClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => {
                  onLogoutClose();
                  handleSignOut(); // your logout function
                }}
              >
                Sign Out
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </>
  );
}

// Mobile Sidebar Component
export function SidebarResponsive({ routes = [], children }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const btnRef = React.useRef();
  const [profile, setProfile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const empCode = localStorage.getItem("emp_code");

  // Fetch user profile data for mobile sidebar
  useEffect(() => {
    const fetchProfileInfoDetails = async () => {
      try {
        if (empCode) {
          const data = await fetchProfileInfo(empCode);
          setProfile(data);
        }
      } catch (error) {
        console.error("Error fetching profile info:", error);
      }
    };

    fetchProfileInfoDetails();
  }, [empCode]);

  useEffect(() => {
    const loadProfilePicture = async () => {
      const imageUrl = await fetchUserProfilePicture(empCode);
      if (imageUrl) setProfilePhoto(imageUrl);
    };
    loadProfilePicture();
  }, [empCode]);

  // Filter routes for mobile sidebar
  const filteredRoutes = Array.isArray(routes) ? routes.map(route => {
    if (!route) return null;
    if (route.category && route.items) {
      return {
        ...route,
        items: route.items.filter(item => item && item.name !== "Sign Out" && item.name !== "My Profile")
      };
    }
    return route;
  }).filter(route => route && route.name !== "Sign Out" && route.name !== "My Profile") : [];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  // Handle sign out for mobile
  const handleSignOut = () => {
    logoutUser({ navigate, setAuth });
  };


  // Handle my profile navigation for mobile
  const handleMyProfile = () => {
    navigate('/admin/profile');
    onClose();
  };

  // Check if route is active
  const isRouteActive = (routePath) => {
    return location.pathname === routePath;
  };

  // Process routes to get a flat list of all navigable routes
  const getNavigableRoutes = (routesList) => {
    if (!routesList || !Array.isArray(routesList)) {
      return [];
    }

    let navigableRoutes = [];

    routesList.forEach(route => {
      if (!route) return;

      if (route.category && route.items) {
        navigableRoutes = [...navigableRoutes, ...getNavigableRoutes(route.items)];
      }
      else if (route.layout && route.path && route.name !== "Sign Out" && route.name !== "My Profile") {
        navigableRoutes.push({
          name: route.name,
          icon: route.icon,
          fullPath: `${route.layout}/${route.path}`
        });
      }
    });

    return navigableRoutes;
  };

  // Get flat list of navigable routes
  const navigableRoutes = getNavigableRoutes(filteredRoutes);

  // Define background style for consistent coloring
  const bgStyles = {
    background: '#F7FAFC', // Light background color for the light blue area
    minHeight: '100%'
  };

  return (
    <>
      {/* Mobile Hamburger Menu Button */}
      <Flex
        display={{ base: "flex", md: "none" }}
        alignItems="center"
        position="fixed"
        top="0"
        left="0"
        zIndex="1000"
        pt="4"
        pl="4"
      >
        <IconButton
          ref={btnRef}
          aria-label="Toggle Menu"
          icon={<HamburgerIcon />}
          onClick={onOpen}
          variant="ghost"
          marginRight="8px"
        />
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left" finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody padding="0" pt="2">
            {/* User Profile Section - Updated to use profile data */}
            <VStack spacing={1} align="center" mb={3}>
              <Avatar
                size="xl"
                bg="green.50"
                color="green.700"
                border="1px solid #C6F6D5"
                src={profilePhoto || undefined}
                name={profile ? profile.user_name : "Loading..."}
              />
              <Text fontWeight="bold" fontSize="md">{profile ? profile.user_name : "Loading..."}</Text>
              <Text fontSize="xs" color="gray.500">{profile ? profile.email_Id : "Loading..."}</Text>

              {/* User Actions for Mobile - Updated to separate sign out */}
              <HStack spacing={3} mt={2}>
                <IconButton
                  aria-label="Sign Out"
                  icon={
                    <Box as="span" display="flex" alignItems="center" justifyContent="center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                    </Box>
                  }
                  onClick={handleSignOut}
                  variant="outline"
                  colorScheme="red"
                  size="sm"
                  mr={2}
                />

                <IconButton
                  aria-label="My Profile"
                  icon={<Avatar size="xs" name={profile ? profile.user_name : "User"} />}
                  onClick={handleMyProfile}
                  variant="outline"
                  colorScheme="green"
                  size="sm"
                />
              </HStack>
            </VStack>

            {/* Mobile Navigation Links */}
            <Box px="3">
              <VStack spacing={1} align="start" w="100%">
                {navigableRoutes.map((route, index) => (
                  <Flex
                    key={index}
                    w="100%"
                    py="2"
                    px="3"
                    cursor="pointer"
                    borderRadius="md"
                    bg={isRouteActive(route.fullPath) ? "green.50" : "transparent"}
                    color={isRouteActive(route.fullPath) ? "green.600" : "inherit"}
                    _hover={{ bg: isRouteActive(route.fullPath) ? "green.100" : "green.100" }}
                    onClick={() => handleNavigation(route.fullPath)}
                    alignItems="center"
                  >
                    {route.icon}
                    <Text ml="3" fontSize="sm" fontWeight={isRouteActive(route.fullPath) ? "bold" : "normal"}>
                      {route.name}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content Area for Mobile */}
      <Box
        pt="60px"
        minH="100vh"
        style={bgStyles}
      >
        <Box p="6" maxW="1200px" mx="auto">
          {children}
        </Box>
      </Box>
    </>
  );
}

// Main Layout Component
function MainLayout({ routes = [], children }) {
  return (
    <>
      {/* Desktop Sidebar */}
      <Box display={{ base: "none", md: "block" }} minH="100vh">
        <Sidebar routes={routes}>
          {children}
        </Sidebar>
      </Box>

      {/* Mobile Sidebar */}
      <Box display={{ base: "block", md: "none" }} minH="100vh">
        <SidebarResponsive routes={routes}>
          {children}
        </SidebarResponsive>
      </Box>
    </>
  );
}

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node
};

SidebarResponsive.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node
};

MainLayout.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node
};

export default MainLayout;